/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import Renderable from "App/render/webgl/renderables/renderable";
import WebGLRenderGraph, {ERenderFiler} from "App/render/webgl/render_graph";
import Pass from "App/render/webgl/passes/pass";
import WebGLContext from "App/web_gl_context";
import {FLOAT_PRECISION} from "App/math";
import Factory from "App/render/webgl/renderables/factory";
import IScene from "js_interface/src/scene_interface";
import RenderOptionNode from "App/render/webgl/nodes/render_option_node";
import {EOutlinesStyle} from "js_interface/src/modules/render_settings/scene_render_settings_enums";

export default
class RenderOption extends Renderable {

    public static TypeName: string = "RenderOption";
    protected _props: any;
    private cache: { glOrder: GLenum; polygonOffsetEnabled: boolean; culling: boolean; glodPositionOffset: Float32Array };

    constructor(aSceneAPI: IScene, aRenderGraph: WebGLRenderGraph) {
        super(aSceneAPI, aRenderGraph, null);

        this._props = null;

        // cached values
        this.cache = {
            polygonOffsetEnabled: false,
            glOrder: null,
            culling: true,
            glodPositionOffset: null
        };
    }

    public static create(aSceneAPI: IScene, aRenderGraph: WebGLRenderGraph): RenderOption {
        return new RenderOption(aSceneAPI, aRenderGraph);
    }

    public setProperties(aProps: any) {
        let gl = (this._context as WebGLContext).getWebGLRenderingContext();

        this._props = aProps;

        // cache values
        this.cache.polygonOffsetEnabled = (Math.abs(aProps.polygonOffsetFactor) > FLOAT_PRECISION || Math.abs(aProps.polygonOffsetUnits) > FLOAT_PRECISION);
        this.cache.glOrder = aProps.vertexOrdering === "CCW" ? gl.CCW : gl.CW;
        this.cache.culling = aProps.vertexOrdering === "UNKNOWN" ? false : aProps.culling;
        this.cache.glodPositionOffset = null;
        if (aProps.glodXOffset !== null) {
            this.cache.glodPositionOffset = new Float32Array([aProps.glodXOffset, aProps.glodYOffset]);
        }
    }

    public render(aRenderPass: Pass) {
        let shader: any = aRenderPass.getActiveShaderRenderable();
        let gl = (this._context as WebGLContext).getWebGLRenderingContext();

        let cull = (!this._sceneAPI.renderSettingsModule.getForceBackFaceCullingOff()) && this.cache.culling;
        if (cull) {
            gl.enable(gl.CULL_FACE);
        } else {
            gl.disable(gl.CULL_FACE);
        }

        if (this.cache.polygonOffsetEnabled) {
            gl.enable(gl.POLYGON_OFFSET_FILL);
            gl.polygonOffset(this._props.polygonOffsetFactor, this._props.polygonOffsetUnits);
        } else {
            gl.disable(gl.POLYGON_OFFSET_FILL);
        }

        gl.frontFace(this.cache.glOrder);

        gl.uniform1i(shader.uniformLocations["u_material.two_sided_lighting"], !cull ? 1 : 0);

        gl.uniform1f(shader.uniformLocations.u_clippable, this._props.clippable);

        if (this.cache.glodPositionOffset) {
            gl.uniform2fv(shader.uniformLocations.u_glod_position_offset, this.cache.glodPositionOffset);
        }

        if (shader.uniformLocations.u_blackwhite_outlines !== undefined) {

            if (this._props.notOutline) {
                gl.uniform1i(shader.uniformLocations.u_blackwhite_outlines, 2); // color
            }
            else {
                gl.uniform1i(shader.uniformLocations.u_blackwhite_outlines, 2); // color

                if (this._sceneAPI.renderSettingsModule.getOutlinesStyle() === EOutlinesStyle.BLACK_AND_WHITE) {
                    if (this._sceneAPI.renderSettingsModule.getWireframeRendering()) {
                        gl.uniform1i(shader.uniformLocations.u_blackwhite_outlines, this._sceneAPI.backgroundModule.isBackgroundColorLight() ? 0 : 1);
                    } else {
                        gl.uniform1i(shader.uniformLocations.u_blackwhite_outlines, 3); // only black in shading mode
                    }
                }
            }
        }
    }
}

Factory.register(RenderOption);
